import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import React from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Store } from 'react-notifications-component';

import { getCurrentLanguage } from '@/shared/config/i18n.config';
import { persistLanguagePreference } from '@/shared/services/user-preference-sync.service';
interface LanguageSwitcherProps {
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const currentLanguage = getCurrentLanguage();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (language: 'en' | 'ar') => {
    void persistLanguagePreference(language).then((success) => {
      if (!success) {
        Store.addNotification({
          id: 'language-preference-save-failed',
          title: 'Language',
          message: 'Could not save language preference. UI updated locally.',
          type: 'warning',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut']
        });
      }
    });
    handleClose();
  };

  const languages = [
    {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      countryCode: 'EG'
    },
    {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      countryCode: 'GB'
    }
  ];

  const currentLang = languages.find((lang) => lang.code === currentLanguage);

  return (
    <Box className={className}>
      <IconButton
        onClick={handleClick}
        sx={{
          color: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          },
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
        aria-label="language switcher"
      >
        <ReactCountryFlag
          countryCode={currentLang?.countryCode || 'GB'}
          svg
          style={{
            width: '2rem',
            height: '1.5rem',
            borderRadius: '2px'
          }}
          title={currentLang?.name}
        />
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1, color: 'text.primary' }}
        >
          {currentLang?.nativeName}
        </Typography>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code as 'en' | 'ar')}
            selected={currentLanguage === language.code}
            sx={{
              minWidth: 120,
              direction: language.code === 'ar' ? 'rtl' : 'ltr',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <ReactCountryFlag
              countryCode={language.countryCode}
              svg
              style={{
                width: '2rem',
                height: '1.5rem',
                borderRadius: '2px'
              }}
              title={language.name}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: currentLanguage === language.code ? 'bold' : 'normal',
                textAlign: language.code === 'ar' ? 'right' : 'left',
                fontSize: '1rem'
              }}
            >
              {language.nativeName}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
